import axios from 'axios';
import { PendulumLogoType } from '../consts/pendulumLogo';
import { baseClient, baseUrl } from './baseRequest';
import { Theme } from '../types';

export type GiftTheme = Theme;

export type GiftKlydoAnimationSizes = {
  xs: string;
  s: string;
  m: string;
  l: string;
};

export type GiftKlydo = {
  id: string;
  loopUrl: string;
  theme: GiftTheme;
  klydoAnimation?: GiftKlydoAnimationSizes;
  photoUrl?: string;
  name?: string;
};

export type Gift = {
  giftId: string;
  orderName: string;
  timeZone?: string;
  klydoId: string;
  senderName?: string;
  date?: number;
  senderImageUrl?: string;
  greetingContent?: string;
  pendulumLogoUrl?: string;
  pendulumLogoType?: PendulumLogoType;
  giftTerms?: {
    accepted: boolean;
    signedTimestamp: number | null;
  };
};

export type PopulatedGift = Gift & {
  klydo: GiftKlydo;
};

export type GiftInformation = {
  gift?: PopulatedGift;
  clockInfo?: {
    id?: string;
    idf?: string;
    registerProduct?: boolean;
  };
};

export type KlydoGiftProps = Gift & {
  clockId: string;
};

export type GiftCategory = {
  name: string;
  categoryImageUrl: string;
  klydoIds: string[];
  klydos: GiftKlydo[];
  pendulumLogoUrl?: string;
};

export enum GreetingTone {
  CUTE = 'Cute',
  EDGY = 'Edgy',
  FUNNY = 'Funny',
  INSPIRATIONAL = 'Inspirational',
  RELIGIOUS = 'Religious',
  ROMANTIC = 'Romantic',
  SHORT_SIMPLE = 'Short & Simple',
  THOUGHTFUL_NICE = 'Thoughtful & Nice',
}

export type GenerateGreetPayload = {
  recipientName: string;
  eventDescription: string;
  tone?: GreetingTone;
};

const giftClient = {
  upsertGift: async (input: KlydoGiftProps): Promise<void> => {
    return baseClient.put({ url: '/gift', body: input });
  },
  listCategories: async (): Promise<GiftCategory[]> => {
    return baseClient.get({ url: '/gift-categories' });
  },
  generateGreet: async (payload: GenerateGreetPayload): Promise<string> => {
    return baseClient.post({ url: '/gift/generate-greet', body: payload });
  },
  getGift: async (params: {
    clockId?: string;
    clockIdf?: string;
    orderName?: string;
  }): Promise<GiftInformation> => {
    const queryParams = new URLSearchParams();
    if (params.clockId) queryParams.set('clock-id', params.clockId);
    if (params.clockIdf) queryParams.set('clock-idf', params.clockIdf);
    if (params.orderName) queryParams.set('order-name', params.orderName);
    return baseClient.get({ url: `/gift?${queryParams.toString()}` });
  },
  uploadSenderImage: async (file: string): Promise<{ url: string }> => {
    const response = await axios.post(`${baseUrl}/gift/upload-sender-image`, {
      file,
    });
    return response.data;
  },
  uploadPendulumLogo: async (
    file: string,
    pendulumLogoType?: PendulumLogoType,
  ): Promise<{ url: string }> => {
    const response = await axios.post(`${baseUrl}/gift/upload-pendulum-logo`, {
      file,
      pendulumLogoType,
    });
    return response.data;
  },
};

export default giftClient;
